import type { OrgProject } from '@p2b/contract';
import type { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ErrorNote } from '../../App.js';
import { formatDay, label, useI18n } from '../../i18n.js';
import { Icon } from '../../ui.js';
import { projectPlace } from '../Home.js';
import { EmptyState, filterProjects, projectState, relativeDay, type ProjectState } from '../orgParts.js';
import { Segmented } from './widgets.js';

/** The toolbar (search, state) appears once a register is long enough to need it. */
const TOOLBAR_FROM = 6;

/** The organization's projects as a register: one row each, the newest publication and the last activity. */
export function ProjectsTab({
  query,
  onCreate,
}: {
  query: UseQueryResult<OrgProject[]>;
  onCreate?: () => void;
}) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [state, setState] = useState<ProjectState | 'all'>('all');
  if (query.error) return <ErrorNote error={query.error} />;
  if (!query.data) return <p className="muted">{t('loading')}</p>;
  const all = query.data;
  if (all.length === 0)
    return (
      <EmptyState
        text={t('noProjects')}
        action={
          onCreate && (
            <button className="primary" onClick={onCreate}>
              {t('newProject')}
            </button>
          )
        }
      />
    );
  const list = filterProjects(all, q, state);
  const count = (s: ProjectState) => all.filter((p) => projectState(p) === s).length;

  return (
    <section>
      {all.length >= TOOLBAR_FROM && (
        <div className="toolbar">
          <label className="search grow-sm">
            <Icon.Search className="search-icon" />
            <input
              type="search"
              aria-label={t('searchProjects')}
              placeholder={t('searchProjects')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <Segmented
            label={t('status')}
            value={state}
            onChange={setState}
            options={[
              { value: 'all', label: t('all') },
              { value: 'inProgress', label: t('inProgress'), count: count('inProgress') },
              { value: 'delivered', label: t('delivered'), count: count('delivered') },
            ]}
          />
        </div>
      )}
      {list.length === 0 ? (
        <p className="empty">{t('noMatch')}</p>
      ) : (
        <div className="sheet">
          <table className="stack-sm org-table">
            <thead>
              <tr>
                <th>{t('projects')}</th>
                <th>{t('status')}</th>
                <th className="hide-sm">{t('latestPublication')}</th>
                <th className="hide-sm">{t('lastActivity')}</th>
                <th className="hide-sm">{t('yourRole')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => {
                const href = `/projects/${p.org}/${p.slug}`;
                const st = projectState(p);
                return (
                  <tr key={p.id} className="link-row" onClick={() => navigate(href)}>
                    <td className="cell-grow">
                      <Link to={href} className="cell-strong row-link" onClick={(e) => e.stopPropagation()}>
                        {p.name}
                      </Link>
                      {projectPlace(p) && <div className="sub">{projectPlace(p)}</div>}
                    </td>
                    <td>
                      <span className={`tag dot${st === 'delivered' ? ' ok' : ''}`}>{t(st)}</span>
                    </td>
                    <td className="hide-sm">
                      {p.latestPublication ? (
                        <>
                          <span className="pub-line">
                            <Icon.Model width={15} height={15} />
                            {p.latestPublication.title || p.latestPublication.revisionLabel}
                          </span>
                          <div className="sub">{formatDay(p.latestPublication.publishedAt, lang)}</div>
                        </>
                      ) : (
                        <span className="muted">{t('noPublication')}</span>
                      )}
                    </td>
                    <td className="hide-sm nowrap">
                      {p.lastActivityAt ? (
                        relativeDay(p.lastActivityAt, lang, t)
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="hide-sm">
                      {label(t, 'role', p.role) || <span className="muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
